import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Reveal, Eyebrow } from "@/components/primitives";

export default function Purpose({ showButton = true }) {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const y2 = useTransform(scrollYProgress, [0, 1], [80, -20]);

  return (
    <section id="who-we-are" className="bg-paper px-6 py-24 lg:px-12 lg:py-36" data-testid="purpose-section">
      <div className="mx-auto grid max-w-[1440px] gap-16 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow index="01">Who we are</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-8 font-serif text-3xl leading-[1.15] text-ink sm:text-4xl lg:text-[3.4rem]" data-testid="purpose-statement">
              ARA Turnkey is an end-to-end firm that designs{" "}
              <em className="italic text-maroon">experiences</em> built to unlock the{" "}
              <em className="italic">highest potential</em> of trailblazing companies.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 flex max-w-xl flex-col gap-6 border-l-2 border-maroon pl-6">
              <p className="text-base leading-relaxed text-ink/70">
                Every space we deliver begins with what the output needs to be produced in that space, and then
                with how people will feel in it, and ends with the engineering precision that makes that feeling
                last. One team holds the pencil and the pipe chart, from first sketch to final snag.
              </p>
              <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-navy">
                Strategy · Design · Build · General Contracting
              </p>
            </div>
          </Reveal>
          {showButton && (
            <Reveal delay={0.3}>
              <button
                onClick={() => navigate("/about")}
                data-testid="about-us-button"
                className="group mt-10 inline-flex items-center gap-3 rounded-full border border-ink/30 px-8 py-3.5 text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-navy-deep hover:text-paper"
              >
                About Us
                <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
              </button>
            </Reveal>
          )}
        </div>

        <div ref={ref} className="relative lg:col-span-5">
          <motion.div style={{ y: y1 }} className="relative z-10">
            <img
              src="https://images.unsplash.com/photo-1759462692354-404b2c995c99?q=80&w=1400&auto=format&fit=crop"
              alt="Premium modern office lobby with contemporary art installation"
              className="aspect-[4/5] w-full object-cover"
              loading="lazy"
              data-testid="purpose-image-main"
            />
          </motion.div>
          <motion.div
            style={{ y: y2 }}
            className="absolute -bottom-16 -left-6 z-20 hidden w-1/2 border-[10px] border-paper shadow-2xl sm:block lg:-left-16"
          >
            <img
              src="https://images.pexels.com/photos/5008401/pexels-photo-5008401.jpeg?auto=compress&cs=tinysrgb&w=1000"
              alt="Upscale office lounge with glass walls and indoor plants"
              className="aspect-[4/3] w-full object-cover"
              loading="lazy"
              data-testid="purpose-image-secondary"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
